import { DEFAULT_WORK_ITEM_PRIORITY, MAX_WORK_ITEM_PRIORITY } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { NONE } from '@/components/desk-dialogs';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useAgents, useApiMutation, useDesks } from '@/lib/hooks';
import { inSentence, t } from '@/lib/i18n';
import { parsePriority } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

export function CreateWorkItemDialog({
  workspaceId,
  deskId: fixedDeskId,
  parentWorkItemId,
  defaultAgentId,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  workspaceId: string;
  deskId?: string;
  parentWorkItemId?: string;
  defaultAgentId?: string | null;
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const desks = useDesks(workspaceId);
  const agents = useAgents(workspaceId);
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const [deskId, setDeskId] = useState(fixedDeskId ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [approvalRule, setApprovalRule] = useState('');
  const [priority, setPriority] = useState(String(DEFAULT_WORK_ITEM_PRIORITY));
  const [agentId, setAgentId] = useState<string>(defaultAgentId ?? NONE);
  const [ready, setReady] = useState(true);
  const [extType, setExtType] = useState('');
  const [extId, setExtId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [typeId, setTypeId] = useState<string>(NONE);

  const effectiveDeskId = fixedDeskId ?? deskId;
  // The desk's own kinds of work (docs/features/self-organizing-desks.md).
  const types = useQuery({
    queryKey: ['desk', effectiveDeskId, 'work-item-types'],
    queryFn: () => api.desks.workItemTypes(effectiveDeskId),
    enabled: open && !!effectiveDeskId,
  });
  // The desk's own word for its items ("Case", "Ticket"), docs/features/desks.md.
  const label = (desks.data ?? []).find((d) => d.id === effectiveDeskId)?.workItemLabel.singular ?? t('Work Item');
  // German capitalises nouns, so there the desk's word keeps its case.
  const noun = inSentence(label);

  const reset = () => {
    setTitle('');
    setDescription('');
    setGoal('');
    setApprovalRule('');
    setPriority(String(DEFAULT_WORK_ITEM_PRIORITY));
    setAgentId(defaultAgentId ?? NONE);
    setReady(true);
    setExtType('');
    setExtId('');
    setDueAt('');
    setTypeId(NONE);
  };

  const create = useApiMutation(
    () =>
      api.workItems.create({
        deskId: effectiveDeskId,
        title: title.trim(),
        description: description.trim() || undefined,
        goal: goal.trim() || undefined,
        // Empty on a sub-item: it keeps the parent's rule.
        approvalRule: approvalRule.trim() || (parentWorkItemId ? undefined : null),
        priority: parsePriority(priority),
        assignedAgentId: agentId === NONE ? null : agentId,
        parentWorkItemId: parentWorkItemId ?? null,
        externalReference: extType.trim() && extId.trim() ? { type: extType.trim(), id: extId.trim() } : null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        typeId: typeId === NONE ? null : typeId,
        ready,
      }),
    {
      success: t('{item} created', { item: label }),
      invalidate: (wi) => [
        qk.workItemsAll(workspaceId),
        qk.overview(workspaceId),
        qk.desk(wi.deskId),
        ...(parentWorkItemId ? [qk.workItem(parentWorkItemId)] : []),
      ],
      onSuccess: (wi) => {
        setOpen(false);
        reset();
        void navigate({ to: '/w/$workspaceId/work-items/$workItemId', params: { workspaceId, workItemId: wi.id } });
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim() && effectiveDeskId) create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{parentWorkItemId ? t('Create sub-item') : t('Create {item}', { item: noun })}</DialogTitle>
            <DialogDescription>
              {label === 'Work Item'
                ? t('A Work Item is one concrete, self-contained piece of work. The desk’s Lead takes it in, plans it and runs its agents on it — or the agent you name starts right away. Only the title is required.')
                : t('A {item} (a Work Item) is one concrete, self-contained piece of work. The desk’s Lead takes it in, plans it and runs its agents on it — or the agent you name starts right away. Only the title is required.', { item: noun })}
            </DialogDescription>
          </DialogHeader>
          {!fixedDeskId && (
            <Field label={t('Desk')}>
              <Select value={deskId} onValueChange={setDeskId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Which Desk does this task belong to?')} />
                </SelectTrigger>
                <SelectContent>
                  {(desks.data ?? []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={t('Title')}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('e.g. Answer customer request CASE-123')} autoFocus required />
          </Field>
          <Field label={t('Description')} hint={t('What should be done? Add all context the agent needs — links, background, steps.')} optional>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder={t('e.g. Clone the shop repository and find out why invoices for CASE-123 round the VAT wrong.')}
            />
          </Field>
          <Field label={t('Goal')} hint={t('When is it done?')} optional>
            <Textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              rows={2}
              placeholder={t('e.g. Done when the root cause is found and a fix is proposed with a test.')}
            />
          </Field>
          <Field
            label={t('Ask for approval')}
            hint={
              parentWorkItemId
                ? t('Leave empty to keep the rule of the parent item.')
                : t('Where should the agent stop and ask you before it continues? Before that point it works on its own. Empty: only the Desk guidelines decide.')
            }
            optional
          >
            <Textarea
              value={approvalRule}
              onChange={(e) => setApprovalRule(e.target.value)}
              rows={2}
              placeholder={t('e.g. Analysing the case and the code is fine. Before you change code or write to the customer, ask me.')}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Agent')} optional>
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose an agent')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('The Lead decides')}</SelectItem>
                  {(agents.data ?? []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('Priority')} hint={t('0–100, 50 = normal. Higher is worked on first.')}>
              <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} min={0} max={MAX_WORK_ITEM_PRIORITY} step={1} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Due')} optional hint={t("When it passes, the Lead is woken (for a sub-item: the parent's Lead).")}>
              <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </Field>
            {(types.data?.length ?? 0) > 0 && (
              <Field label={t('Kind of work')} optional>
                <Select value={typeId} onValueChange={setTypeId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('None')}</SelectItem>
                    {types.data!.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('External reference: type')} optional hint={t('e.g. ticket, case, issue')}>
              <Input value={extType} onChange={(e) => setExtType(e.target.value)} placeholder="case" />
            </Field>
            <Field label={t('External reference: ID')} optional>
              <Input value={extId} onChange={(e) => setExtId(e.target.value)} placeholder="CASE-123" />
            </Field>
          </div>
          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Switch id="wi-ready" checked={ready} onCheckedChange={setReady} className="mt-0.5" />
            <div className="grid gap-0.5">
              <Label htmlFor="wi-ready">{t('Start right away')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('With an agent named, it starts right away; without one, the Lead takes it in. Off: it stays “Created”.')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!title.trim() || !effectiveDeskId}>
              {t('Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
